import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useApp } from "@/context/AppContext";
import { MissionProgress } from "@/components/MissionProgress";
import { LETTERS, LETTER_ORDER } from "@/data/letters";
import { Lock, Play, CheckCircle2, Star } from "lucide-react";

function MissionCard({ letter, index, progress, onOpen }) {
  const data = LETTERS[letter];
  const locked = !progress?.unlocked;
  const mastered = progress?.mastered;

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className={`relative overflow-hidden rounded-3xl border p-6 sm:p-7 transition-all ${
        locked
          ? "border-[#E5DECF] bg-[#F2ECE1]/60"
          : "border-[#E5DECF] bg-white hover:shadow-xl hover:-translate-y-1"
      }`}
      data-testid={`mission-card-${letter}`}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-widest font-bold text-[#6B7280]">
          Mission {index + 1}
        </span>
        {mastered ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-[#10B981]/15 px-3 py-1 text-xs font-bold text-[#047857]" data-testid={`status-${letter}`}>
            <CheckCircle2 size={14} /> Mastered
          </span>
        ) : locked ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-[#94A3B8]/20 px-3 py-1 text-xs font-bold text-[#64748B]" data-testid={`status-${letter}`}>
            <Lock size={14} /> Locked
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full bg-[#F59E0B]/15 px-3 py-1 text-xs font-bold text-[#B45309]" data-testid={`status-${letter}`}>
            <Play size={14} /> Ready
          </span>
        )}
      </div>

      <div className="mt-4 flex items-end justify-between">
        <div>
          <div
            className="font-cursive leading-none text-[#0D9488]"
            style={{ fontSize: "5.5rem", opacity: locked ? 0.35 : 1 }}
          >
            {letter}
          </div>
          <div className="mt-1 font-heading text-lg font-bold text-[#1F2937]">{data.displayName}</div>
        </div>
        {mastered && (
          <div className="flex gap-0.5">
            {[0, 1, 2].map((i) => (
              <Star key={i} size={18} fill="#F59E0B" color="#F59E0B" />
            ))}
          </div>
        )}
      </div>

      {!locked && progress?.attempts > 0 && (
        <div className="mt-2 text-sm font-body text-[#6B7280]">
          Best score: <span className="font-bold text-[#1F2937]">{progress.best_score}%</span>
        </div>
      )}

      <button
        disabled={locked}
        onClick={() => onOpen(letter)}
        data-testid={`open-mission-${letter}`}
        className={`mt-5 inline-flex w-full items-center justify-center gap-2 rounded-2xl px-5 py-3.5 text-base font-bold transition-all active:translate-y-0.5 ${
          locked
            ? "cursor-not-allowed bg-[#E5DECF] text-[#94A3B8]"
            : mastered
            ? "bg-[#F2ECE1] text-[#1F2937] hover:bg-[#E5DECF]"
            : "bg-[#0D9488] text-white shadow-md hover:bg-[#0B7F74] hover:shadow-lg"
        }`}
      >
        {locked ? (
          <>Complete previous letter</>
        ) : mastered ? (
          <>Practice Again</>
        ) : (
          <>
            <Play size={18} /> Start Mission
          </>
        )}
      </button>
    </motion.div>
  );
}

export default function Dashboard() {
  const { user, progress, refreshProgress } = useApp();
  const navigate = useNavigate();
  useEffect(() => { refreshProgress && refreshProgress(); }, [refreshProgress]);
  const mastered = progress.filter((p) => p.mastered).length;

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-10">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
        <div className="text-xs uppercase tracking-widest font-bold text-[#F59E0B]">Cursive Missions</div>
        <h1 className="mt-1 font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-[#1F2937]">
          Welcome{user ? `, ${user.name}` : ""}!
        </h1>
        <p className="mt-2 font-body text-base sm:text-lg text-[#6B7280] max-w-2xl">
          Welcome to <span className="font-cursive text-2xl text-[#0D9488]">ZoodLeap</span>. Master each
          letter with 80% or higher to unlock the next mission.
        </p>
      </motion.div>

      <div className="mt-8">
        <MissionProgress mastered={mastered} total={LETTER_ORDER.length} />
      </div>

      <div className="mt-8 grid gap-6 sm:gap-8 md:grid-cols-3">
        {LETTER_ORDER.map((letter, i) => (
          <MissionCard
            key={letter}
            letter={letter}
            index={i}
            progress={progress.find((p) => p.letter === letter)}
            onOpen={(l) => navigate(`/lesson/${l}`)}
          />
        ))}
      </div>

      {mastered === LETTER_ORDER.length && (
        <div className="mt-8 rounded-3xl border-2 border-[#F59E0B] bg-[#F59E0B]/10 p-6 text-center">
          <div className="font-heading text-2xl font-extrabold text-[#B45309]">Mission Complete! 🎖️</div>
          <p className="mt-1 font-body text-[#1F2937]">You mastered a, b, and c! Visit your progress to see your badge.</p>
        </div>
      )}
    </div>
  );
}
